import clsx from "clsx";
import { useId, useState } from "react";

import { useApiQuery } from "../../../api/hooks";
import type { ElementKind, ElementSummary, Id } from "../../../api/types";
import { Button, IconButton } from "../../../ui";
import { isGoneState, useDebounced, useElementList } from "../hooks";
import { kindWord } from "../lib/words";

/** The value of the "none" line of a picker, kept apart from "" which means "not answered yet". */
export const NONE = "__none__";

/**
 * The line of a list for each element: its name, and nothing else. The code is an internal reference (UI-16): it is added
 * in brackets only for elements that share a name, because then the name alone does not tell them apart.
 */
export function optionLabels(items: ReadonlyArray<Pick<ElementSummary, "id" | "name" | "code">>): Map<string, string> {
  const sameName = new Map<string, number>();
  for (const item of items) {
    const key = item.name.trim().toLowerCase();
    sameName.set(key, (sameName.get(key) ?? 0) + 1);
  }
  return new Map(
    items.map((item) => {
      const shared = (sameName.get(item.name.trim().toLowerCase()) ?? 0) > 1;
      return [item.id, shared && item.code && item.code !== item.name ? `${item.name} (${item.code})` : item.name] as const;
    }),
  );
}

interface PickerProps {
  label: string;
  hint?: string;
  error?: string | null;
  required?: boolean;
  /** The kinds of element to choose from. */
  kinds: readonly ElementKind[];
  value: string;
  onChange: (id: string, summary: ElementSummary | null) => void;
  /** A line for "no element", worded for the question ("None: it is a top-level item"). Its value is `NONE`. */
  noneLabel?: string;
  placeholder?: string;
  /** Ids to leave out of the list. */
  exclude?: readonly Id[];
  filter?: (item: ElementSummary) => boolean;
  disabled?: boolean;
  className?: string;
}

/**
 * Choose one element of the discipline. The list is read from the server (up to 100); when there are more, a search
 * box narrows it. The element already chosen stays in the list even when it is beyond the first 100.
 */
export function ElementPicker({ label, hint, error, required, kinds, value, onChange, noneLabel, placeholder = "Choose one", exclude, filter, disabled, className }: PickerProps) {
  const id = useId();
  const [search, setSearch] = useState("");
  const q = useDebounced(search.trim(), 250);
  const first = useElementList(kinds[0] ?? null, { q });
  const second = useElementList(kinds[1] ?? null, { q, enabled: kinds.length > 1 });
  const third = useElementList(kinds[2] ?? null, { q, enabled: kinds.length > 2 });
  const fourth = useElementList(kinds[3] ?? null, { q, enabled: kinds.length > 3 });
  const lists = [first, second, third, fourth].slice(0, Math.min(kinds.length, 4));

  const merged: ElementSummary[] = lists.flatMap((list) => list.items);
  const omitted = new Set(exclude ?? []);
  const items = merged.filter((item) => !omitted.has(item.id) && (!filter || filter(item)));
  const truncated = lists.some((list) => list.total > list.read) || search !== "";
  const chosen = value && value !== NONE ? value : null;
  const known = chosen ? items.find((item) => item.id === chosen) : undefined;
  const missing = useApiQuery("element", { id: chosen ?? "" }, { enabled: !!chosen && !known, staleTime: 30_000 });
  const loading = lists.some((list) => list.isPending);

  const showKind = kinds.length > 1;
  const labels = optionLabels(items);
  const describe = (item: ElementSummary) => (showKind ? `${labels.get(item.id) ?? item.name}, ${kindWord(item.kind).toLowerCase()}` : (labels.get(item.id) ?? item.name));

  return (
    <div className={clsx("field", className)}>
      <label htmlFor={id} className="field__label">
        {label}
        {required && <span className="field__required"> (needed)</span>}
      </label>
      {hint && (
        <p id={`${id}-hint`} className="field__hint muted">
          {hint}
        </p>
      )}
      {truncated && (
        <input
          type="search"
          className="field__input picker__search"
          aria-label={`Search the list of ${label.toLowerCase()}`}
          placeholder="Type to narrow the list"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      )}
      <select
        id={id}
        className="field__input"
        value={value}
        disabled={disabled}
        aria-invalid={error ? true : undefined}
        aria-describedby={[hint ? `${id}-hint` : null, error ? `${id}-error` : null].filter(Boolean).join(" ") || undefined}
        onChange={(event) => {
          const next = event.target.value;
          onChange(next, items.find((item) => item.id === next) ?? null);
        }}
      >
        <option value="">{loading ? "Loading the list" : placeholder}</option>
        {noneLabel && <option value={NONE}>{noneLabel}</option>}
        {chosen && !known && <option value={chosen}>{missing.data?.element.name ?? "The element chosen before"}</option>}
        {items.map((item) => (
          <option key={item.id} value={item.id}>
            {describe(item)}
          </option>
        ))}
      </select>
      {!loading && items.length === 0 && !noneLabel && <p className="field__hint muted">Nothing to choose from yet.</p>}
      {error && (
        <p id={`${id}-error`} role="alert" className="field__error">
          {error}
        </p>
      )}
    </div>
  );
}

interface MultiProps {
  label: string;
  hint?: string;
  error?: string | null;
  kinds: readonly ElementKind[];
  values: readonly Id[];
  onChange: (ids: Id[]) => void;
  addLabel?: string;
  filter?: (item: ElementSummary) => boolean;
  className?: string;
}

/** Several elements of the discipline, added one at a time and taken out again. */
export function MultiElementPicker({ label, hint, error, kinds, values, onChange, addLabel = "Add", filter, className }: MultiProps) {
  const [pick, setPick] = useState("");
  return (
    <fieldset className={clsx("field field--group", className)}>
      <legend className="field__label">{label}</legend>
      {hint && <p className="field__hint muted">{hint}</p>}
      {values.length > 0 && (
        <ul className="chip-list">
          {values.map((chosen) => (
            <ChosenChip key={chosen} id={chosen} onRemove={() => onChange(values.filter((v) => v !== chosen))} />
          ))}
        </ul>
      )}
      <div className="row">
        <ElementPicker label={`${label}: pick one to add`} kinds={kinds} value={pick} onChange={(id) => setPick(id)} exclude={values} filter={filter} className="grow" />
        <Button
          variant="secondary"
          disabled={!pick}
          onClick={() => {
            if (pick && !values.includes(pick)) onChange([...values, pick]);
            setPick("");
          }}
        >
          {addLabel}
        </Button>
      </div>
      {error && (
        <p role="alert" className="field__error">
          {error}
        </p>
      )}
    </fieldset>
  );
}

function ChosenChip({ id, onRemove }: { id: Id; onRemove: () => void }) {
  const query = useApiQuery("element", { id }, { staleTime: 30_000 });
  const name = query.data?.element.name ?? "Chosen element";
  return (
    <li className="chip">
      <span>{name}</span>
      <IconButton label={`Take out ${name}`} onClick={onRemove}>
        ×
      </IconButton>
    </li>
  );
}

interface SearchPickerProps {
  label: string;
  hint?: string;
  error?: string | null;
  required?: boolean;
  value: string;
  onChange: (id: string, summary: ElementSummary | null) => void;
  /** Leave out these kinds from the results. */
  exclude?: readonly ElementKind[];
}

/**
 * Choose any element of the discipline by searching for its name. The search reads the discipline's own word
 * index (`find_in_ontology`), so it reaches every kind at once.
 */
export function ElementSearchPicker({ label, hint, error, required, value, onChange, exclude }: SearchPickerProps) {
  const id = useId();
  const [text, setText] = useState("");
  const q = useDebounced(text.trim(), 250);
  const chosen = useApiQuery("element", { id: value }, { enabled: !!value, staleTime: 30_000 });
  const hits = useApiQuery("find_in_ontology", { q, limit: 12 }, { enabled: !value && q.length >= 2, staleTime: 15_000 });
  const results = (hits.data ?? []).filter((hit) => !isGoneState(hit.element.state) && !(exclude ?? []).includes(hit.element.kind));

  if (value) {
    const row = chosen.data?.element;
    return (
      <div className="field">
        <span className="field__label">{label}</span>
        <p className="picked">
          <strong>{row?.name ?? "The element chosen"}</strong> {row && <span className="muted">{kindWord(row.kind)}, {row.code}</span>}{" "}
          <Button variant="quiet" onClick={() => onChange("", null)}>
            Choose another
          </Button>
        </p>
        {error && (
          <p role="alert" className="field__error">
            {error}
          </p>
        )}
      </div>
    );
  }
  return (
    <div className="field">
      <label htmlFor={id} className="field__label">
        {label}
        {required && <span className="field__required"> (needed)</span>}
      </label>
      {hint && <p className="field__hint muted">{hint}</p>}
      <input id={id} type="search" className="field__input" placeholder="Type part of its name" value={text} aria-invalid={error ? true : undefined} onChange={(event) => setText(event.target.value)} />
      {q.length >= 2 && hits.isPending && <p className="muted">Searching</p>}
      {q.length >= 2 && !hits.isPending && results.length === 0 && <p className="muted">No element has that name.</p>}
      {results.length > 0 && (
        <ul className="search-results" aria-label={`Matches for ${label.toLowerCase()}`}>
          {results.map((hit) => (
            <li key={hit.element.id}>
              <Button variant="quiet" onClick={() => onChange(hit.element.id, hit.element)}>
                {hit.element.name} <span className="muted">{kindWord(hit.element.kind)}</span>
              </Button>
            </li>
          ))}
        </ul>
      )}
      {error && (
        <p role="alert" className="field__error">
          {error}
        </p>
      )}
    </div>
  );
}
